import { useState } from 'react';
import { Address, newAddressProp } from '../types/type';
import { useApiKey } from './useApiKey';
import dataProvider from '../../services/api';

interface UseMapHandlerProps {
  selectedAddress: Address | null;
  setSelectedAddress: (address: Address) => void;
  newAddress: newAddressProp;
  setNewAddress: (address: newAddressProp | ((prev: newAddressProp) => newAddressProp)) => void;
  setIsGeocodingLoading: (loading: boolean) => void;
  showSuccessMessage: (message: string) => void;
  showErrorMessage: (message: string) => void;
  handleErrorWithLogout: (error: any) => void;
}

export const useMapHandler = ({
  selectedAddress,
  setSelectedAddress,
  newAddress,
  setNewAddress,
  setIsGeocodingLoading,
  showSuccessMessage,
  showErrorMessage,
  handleErrorWithLogout,
}: UseMapHandlerProps) => {
  const { apiKeyValue, loading } = useApiKey();
  const [openMapPicker, setOpenMapPicker] = useState(false);
  const [currentEditingCoordinates, setCurrentEditingCoordinates] = useState<'existing' | 'new' | null>(null);
  const handleOpenMapPicker = (type: 'existing' | 'new') => {
    if (loading) return;
    setCurrentEditingCoordinates(type);
    setOpenMapPicker(true);
  };

  const handleMapLocationSelect = async (lat: number, lng: number, address?: string) => {
    if (!apiKeyValue || loading) {
      showErrorMessage('API key is not available.');
      return;
    }
    setIsGeocodingLoading(true);
    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${apiKeyValue}`
      );
      const data = await response.json();

      if (data.status === 'OK' && data.results.length > 0) {
        const result = data.results[0];
        const addressComponents = result.address_components;

        const getComponent = (type: string) =>
          addressComponents.find((comp: any) => comp.types.includes(type))?.long_name || '';

        const streetNumber = getComponent('street_number');
        const route = getComponent('route');
        const street = streetNumber && route ? `${streetNumber} ${route}` : route || '';
        const subdivision = getComponent('sublocality') || getComponent('neighborhood') || '';
        const city = getComponent('locality') || '';
        const province = getComponent('administrative_area_level_2') || '';
        const zipCode = getComponent('postal_code') || '';

        let cityId = 0;
        let provinceId = 0;
        let provinceName = province;
        let cityClass = 0;

        if (city) {
          try {
            const { data } = await dataProvider.lookupCitiesUser({ name: city, count: 10, page: 0 });
            if (data.length > 0) {
              const matchedCity = data.find(
                (cityData: any) =>
                  cityData.name.toLowerCase() === city.toLowerCase() &&
                  cityData.province?.name.toLowerCase() === province.toLowerCase()
              ) || data[0];

              cityId = matchedCity.cityId;
              provinceId = matchedCity.provinceId;
              provinceName = matchedCity.province?.name || province;
              cityClass = matchedCity.class;
            } else {
              showErrorMessage(`City "${city}" is invalid. Please enter a valid city.`);
            }
          } catch (error: any) {
            handleErrorWithLogout(error);
            showErrorMessage('Error looking up city. Please enter a valid city.');
          }
        }

        if (currentEditingCoordinates === 'existing' && selectedAddress) {
          setSelectedAddress({
            ...selectedAddress,
            coordinatesLatitude: lat,
            coordinatesLongitude: lng,
            street,
            subdivision,
            city: cityId ? { 
              cityId, 
              name: city, 
              city: {
                cityId,
                provinceId,
                province: {
                  id: provinceId,
                  name: provinceName
                },
                name: city,
                class: cityClass
              } 
            } : selectedAddress.city,
            province: { id: provinceId, name: provinceName },
            zipCode,
          });
        } else if (currentEditingCoordinates === 'new') {
          setNewAddress(prev => ({
            ...prev,
            coordinatesLatitude: lat,
            coordinatesLongitude: lng,
            street,
            subdivision,
            cityId,
            zipCode,
          }));
        }
        showSuccessMessage('Address details updated successfully.');
      } else {
        showErrorMessage('Unable to retrieve address details.');
      }
    } catch (error) {
      handleErrorWithLogout(error);
    } finally {
      setIsGeocodingLoading(false);
      setOpenMapPicker(false);
      setCurrentEditingCoordinates(null);
    }
  };

  const handleCloseMapPicker = () => {
    setOpenMapPicker(false);
    setCurrentEditingCoordinates(null);
  };

  return {
    openMapPicker,
    currentEditingCoordinates,
    handleOpenMapPicker,
    handleMapLocationSelect,
    handleCloseMapPicker,
  };
};